import React from 'react';

/**
 * Case-insensitive substring search helpers for the JSON viewer (and any
 * other "search keys & values" UI). Kept generic and free of styling so
 * they can be reused across viewers/inspectors.
 */

/**
 * Count occurrences of `q` inside `text` (case-insensitive substring).
 * Returns 0 for empty queries so callers can short-circuit cleanly.
 */
export const countOccurrences = (text: string, q: string): number => {
  if (!q) return 0;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  let count = 0;
  let i = 0;
  let idx = lower.indexOf(needle, i);
  while (idx !== -1) {
    count++;
    i = idx + needle.length;
    idx = lower.indexOf(needle, i);
  }
  return count;
};

/**
 * Recursively counts how many times `q` appears inside the formatted
 * representation of `data`. Walks the data with the same shape the tree
 * viewer renders (keys + string/number/boolean/null/undefined primitives)
 * so the count exactly matches what the user sees highlighted.
 *
 * - Keys are counted as JSON-stringified strings (`"foo"`).
 * - String values: JSON-stringified to match the visible quoting/escaping.
 * - Numbers/booleans: rendered as `String(value)`.
 * - `null` / `undefined`: counted as the literal token strings.
 */
export const countMatchesInData = (data: unknown, q: string): number => {
  if (!q) return 0;
  let total = 0;
  const walk = (value: unknown, keyName?: string) => {
    if (keyName !== undefined) {
      total += countOccurrences(JSON.stringify(keyName), q);
    }
    if (value === null) {
      total += countOccurrences('null', q);
      return;
    }
    if (value === undefined) {
      total += countOccurrences('undefined', q);
      return;
    }
    if (typeof value === 'string') {
      total += countOccurrences(JSON.stringify(value), q);
      return;
    }
    if (typeof value === 'number') {
      total += countOccurrences(String(value), q);
      return;
    }
    if (typeof value === 'boolean') {
      total += countOccurrences(String(value), q);
      return;
    }
    if (Array.isArray(value)) {
      for (const v of value) walk(v);
      return;
    }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        walk(v, k);
      }
    }
  };
  walk(data);
  return total;
};

/**
 * Highlight `q` inside `text`. Case-insensitive substring match.
 *
 * Security note: this function returns React nodes whose text is the
 * untrusted JSON value, but each piece is rendered as a plain string
 * inside JSX (`{text.slice(...)}`) so React auto-escapes it. We must NOT
 * introduce `dangerouslySetInnerHTML` or build raw HTML strings here —
 * doing so would turn malicious key/value content into an XSS vector.
 * Keep all output as JSX text children (or className-bearing wrappers
 * around JSX text children) to preserve this safety invariant.
 */
export const highlightText = (text: string, q: string, matchClass: string): React.ReactNode => {
  if (!q) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const out: React.ReactNode[] = [];
  let i = 0;
  let idx = lower.indexOf(needle, i);
  let key = 0;
  while (idx !== -1) {
    if (idx > i) out.push(text.slice(i, idx));
    out.push(<mark key={key++} className={matchClass}>{text.slice(idx, idx + needle.length)}</mark>);
    i = idx + needle.length;
    idx = lower.indexOf(needle, i);
  }
  if (i < text.length) out.push(text.slice(i));
  return <>{out}</>;
};
